import { ApiError } from '../api/client';
import { documentReadFailureMessage, NETWORK_ERROR_MESSAGE } from '../api/messages';

/**
 * In-app previews of office documents (ADR-0033). The content endpoint serves
 * spreadsheets, Word files, CSVs, … as an octet-stream ATTACHMENT (the API
 * origin never renders uploaded markup), so the viewers cannot show those
 * bytes. Instead they ask the server for a read-only JSON preview —
 * GET /api/v1/rooms/:roomId/documents/:documentId/preview — through the same
 * visibility check as the bytes, and render it themselves (a sheet grid, Word
 * HTML through the allowlist rebuild in `SafeHtml`, text, or — for images a
 * browser cannot show (multi-page TIFF scans, HEIC photos) — pages rendered
 * server-side as JPEG).
 */

/** One sheet of a spreadsheet preview — mirrors src/modules/preview `SpreadsheetPreviewSheet`. */
export interface SpreadsheetPreviewSheet {
  name: string;
  hidden: boolean;
  /** Zero-based sheet row / column of `rows[0][0]` (Excel-aligned numbering). */
  startRow: number;
  startColumn: number;
  /** Display text of each cell (number / date formats applied), row-major. */
  rows: string[][];
  /**
   * Workbooks only (absent for CSV, demo data and older cached previews): one
   * string per row, a `1` per numeric / date cell, else `0`. Absent → the grid
   * reads the text ({@link looksNumeric}).
   */
  numeric?: string[];
  totalRows: number;
  totalColumns: number;
  rowsTruncated: boolean;
  columnsTruncated: boolean;
}

/** One page of a page preview — mirrors src/modules/preview `PreviewPage`: a JPEG `data:` URL. */
export interface PreviewPage {
  src: string;
  width: number;
  height: number;
}

export type DocumentPreview =
  | { kind: 'spreadsheet'; sheets: SpreadsheetPreviewSheet[]; sheetsTruncated: boolean }
  | { kind: 'html'; html: string; imagesOmitted: boolean; truncated?: boolean }
  | { kind: 'text'; text: string; truncated: boolean }
  /** `totalPages`: pages in the file; `pagesTruncated`: some are not shown. */
  | { kind: 'pages'; pages: PreviewPage[]; totalPages: number; pagesTruncated: boolean }
  | { kind: 'unsupported'; reason: 'type' | 'unreadable' };

/**
 * GET /rooms/:roomId/documents/:documentId/preview — the read-only office preview.
 * `buyerCopy`: the preview of what BUYERS are served (the redacted copy), for
 * the admin / seller viewer that shows what buyers see (`?copy=buyer`).
 */
export function documentPreviewPath(
  roomId: string,
  documentId: string,
  opts: { buyerCopy?: boolean } = {},
): string {
  const path = `/api/v1/rooms/${roomId}/documents/${documentId}/preview`;
  return opts.buyerCopy ? `${path}?copy=buyer` : path;
}

/**
 * Extensions whose bytes the content endpoint cannot hand a viewer to render,
 * but the preview endpoint can. The server decides the real format from the
 * stored type, so this is only "which endpoint to ask"; a mismatch simply comes
 * back `unsupported`.
 */
const PREVIEW_EXTENSIONS = new Set([
  // Spreadsheets
  'xlsx',
  'xlsm',
  'xls',
  'ods',
  'csv',
  'tsv',
  // Word & rich text
  'docx',
  'doc',
  'rtf',
  // Presentations
  'pptx',
  'ppt',
  // Images browsers cannot render: shown as server-rendered pages
  'tif',
  'tiff',
  'heic',
  'heif',
  // Text served as an attachment by the content endpoint
  'md',
  'markdown',
  'htm',
  'html',
]);

/** Image types no browser renders from bytes; the preview endpoint renders them as pages. */
const SERVER_RENDERED_IMAGE_TYPES = new Set(['image/tiff', 'image/heic', 'image/heif']);

/**
 * True for a Content-Type a viewer must not hand to `<img>` (it would show a
 * broken image): a TIFF or HEIC, which the preview endpoint renders instead.
 * Parameters and case are ignored.
 */
export function isServerRenderedImage(contentType: string | null | undefined): boolean {
  const ct = (contentType ?? '').split(';')[0]?.trim().toLowerCase() ?? '';
  return SERVER_RENDERED_IMAGE_TYPES.has(ct);
}

/** True when `filename` should be shown through the preview endpoint rather than its raw bytes. */
export function usesDocumentPreview(filename: string | null | undefined): boolean {
  const base = (filename ?? '').split(/[\\/]/).pop() ?? '';
  const dot = base.lastIndexOf('.');
  return dot > 0 && PREVIEW_EXTENSIONS.has(base.slice(dot + 1).toLowerCase());
}

/**
 * Fetch a preview (credentialed, same-origin). Failures are {@link ApiError}s
 * with a person-readable message; an abort is rethrown untouched.
 */
export async function fetchDocumentPreview(
  url: string,
  signal: AbortSignal,
  notFoundMessage: string,
  /** Sees the response (e.g. its `X-Document-Version` header, ADR-0056). */
  onResponse?: (res: Response) => void,
): Promise<DocumentPreview> {
  let res: Response;
  try {
    res = await fetch(url, { credentials: 'include', signal });
  } catch (err) {
    if (signal.aborted) throw err;
    throw new ApiError(0, NETWORK_ERROR_MESSAGE, undefined, String(err));
  }
  onResponse?.(res);
  if (!res.ok) {
    // 503 = a storage read failure (temporary), not a missing document.
    throw new ApiError(res.status, documentReadFailureMessage(res.status, notFoundMessage));
  }
  try {
    return (await res.json()) as DocumentPreview;
  } catch (err) {
    if (signal.aborted) throw err;
    throw new ApiError(res.status, NETWORK_ERROR_MESSAGE, undefined, String(err));
  }
}

/** Excel-style column letters for a zero-based column index (0 → A, 26 → AA). */
export function columnLetter(index: number): string {
  let n = index + 1;
  let out = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

/** True when a cell's display text reads as a number/amount (right-aligned in the grid). */
export function looksNumeric(text: string): boolean {
  return /^[-+(]?[$€£¥]?\s?[-(]?\d[\d,]*(\.\d+)?%?\)?$/.test(text.trim());
}

/** Windows-1252's 0x80–0x9F (WHATWG); the five undefined bytes keep their code point. */
const CP1252_HIGH = [
  0x20ac, 0x81, 0x201a, 0x192, 0x201e, 0x2026, 0x2020, 0x2021, 0x2c6, 0x2030, 0x160, 0x2039,
  0x152, 0x8d, 0x17d, 0x8f, 0x90, 0x2018, 0x2019, 0x201c, 0x201d, 0x2022, 0x2013, 0x2014, 0x2dc,
  0x2122, 0x161, 0x203a, 0x153, 0x9d, 0x17e, 0x178,
];

/**
 * The text of a text file served as raw bytes (`.txt`, `.json`, an extensionless
 * README, an `.eml`), in whatever common encoding it was saved: a byte-order
 * mark says UTF-8 / UTF-16LE / UTF-16BE (and is dropped); otherwise strict
 * UTF-8, and bytes that are not valid UTF-8 are read as Windows-1252 — what US
 * Windows exports use. `Blob.text()` always assumed UTF-8, so those showed "�"
 * for every "é" and curly quote. The server decodes the same way
 * (src/modules/extraction/providers/text-extractor.ts).
 */
export function decodeTextBytes(input: ArrayBuffer | Uint8Array): string {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return new TextDecoder('utf-8').decode(bytes.subarray(3));
  }
  if (bytes[0] === 0xff && bytes[1] === 0xfe) {
    return new TextDecoder('utf-16le').decode(bytes.subarray(2));
  }
  if (bytes[0] === 0xfe && bytes[1] === 0xff) {
    return new TextDecoder('utf-16be').decode(bytes.subarray(2));
  }
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    let out = '';
    for (let i = 0; i < bytes.length; i += 0x8000) {
      const codes = Array.from(bytes.subarray(i, i + 0x8000), (b) =>
        b >= 0x80 && b <= 0x9f ? CP1252_HIGH[b - 0x80]! : b,
      );
      out += String.fromCharCode(...codes);
    }
    return out;
  }
}
